import {
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  FingerPrintIcon,
  ServerIcon,
} from "@heroicons/react/20/solid";
import { UserIcon } from "@heroicons/react/24/solid";
import { get } from "lodash-es";
import { FC, useContext } from "react";
import { useIdToken } from "react-firebase-hooks/auth";

import { SvgIcon, Theme } from "@mui/material";
import {
  Avatar,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  Stack,
  useMediaQuery,
} from "@mui/material";

import { firebaseAuth } from "../../configs/firebase";
import { UserContext } from "../../contexts/UserContext";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { usePopover } from "../../hooks/use-popover";
import { useSelector } from "../../store";
import {
  useGetVersionQuery,
  useListClustersQuery,
} from "../../store/pomeriumApi";
import { buildUpgradeUrl } from "../../views/billing/billing-urls";
import { ClusterDropdown } from "./cluster-dropdown";
import { Footer } from "./footer";
import { OrganizationDropdown } from "./organization-dropdown";

export const AccountPopover: FC = () => {
  const navigate = useNavigateWithSearch();
  const { user: pomeriumUser } = useContext(UserContext);
  const { byId } = useSelector((state) => state.organizations);
  const { organizationId } = useSelector((state) => state.current);
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const popover = usePopover<HTMLButtonElement>();
  const [user] = useIdToken(firebaseAuth);
  const { data: apiVersion } = useGetVersionQuery();
  const org = get(byId, organizationId, null);
  const numOrg = Object.values(byId)?.length || 0;
  const { data: clusters } = useListClustersQuery(organizationId, {
    skip: !organizationId,
  });

  const openAccountMenu = () => {
    popover.handleOpen();
  };

  return (
    <>
      <IconButton onClick={openAccountMenu} ref={popover.anchorRef}>
        <Avatar
          src={user?.photoURL || ""}
          variant="rounded"
          sx={{
            height: smDown ? 28 : 40,
            width: smDown ? 28 : 40,
          }}
        />
        <span className="sr-only">Account menu</span>
      </IconButton>
      <Popover
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
        disableScrollLock
        onClose={popover.handleClose}
        open={popover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 240,
            },
          },
        }}
      >
        <List>
          {!pomeriumUser?.needsOnboarding && (
            <List sx={{ backgroundColor: "gray.50" }}>
              <ListSubheader disableSticky className="sr-only">
                <span>
                  {numOrg > 1 ? "Select organization" : "Organization"}
                </span>
              </ListSubheader>
              <Stack sx={{ px: 2 }} direction="row" alignItems="center">
                <ListItemIcon>
                  <SvgIcon fontSize="small">
                    <BuildingOfficeIcon />
                  </SvgIcon>
                </ListItemIcon>
                <OrganizationDropdown />
              </Stack>
            </List>
          )}
          {mdDown && !pomeriumUser?.needsOnboarding && (
            <List sx={{ backgroundColor: "gray.50" }}>
              <ListSubheader disableSticky className="sr-only">
                <span>
                  {clusters?.length || 0 > 1 ? "Select cluster" : "Cluster"}
                </span>
              </ListSubheader>
              <Stack sx={{ px: 2 }} direction="row" alignItems="center">
                <ListItemIcon>
                  <SvgIcon fontSize="small">
                    <ServerIcon />
                  </SvgIcon>
                </ListItemIcon>
                <ClusterDropdown />
              </Stack>
            </List>
          )}
          {!pomeriumUser?.needsOnboarding && (
            <ListItemButton
              sx={{ pt: 2 }}
              onClick={() => {
                popover.handleClose();
                setTimeout(() => {
                  navigate("/app/account", { replace: true });
                }, 100);
              }}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <UserIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText primary={user?.email ?? "Account"} />
            </ListItemButton>
          )}
          {org?.organizationType === "professional" &&
            !pomeriumUser?.needsOnboarding && (
              <ListItemButton
                onClick={() => {
                  popover.handleClose();
                  setTimeout(() => {
                    navigate("/app/organizations", { replace: true });
                  }, 100);
                }}
              >
                <ListItemIcon>
                  <SvgIcon fontSize="small">
                    <BuildingOfficeIcon />
                  </SvgIcon>
                </ListItemIcon>
                <ListItemText primary="Organization" />
              </ListItemButton>
            )}
          {org?.organizationType === "personal" &&
            !pomeriumUser?.needsOnboarding && (
              <ListItemButton
                onClick={() => {
                  popover.handleClose();
                  setTimeout(() => {
                    location.href = buildUpgradeUrl(location.href);
                  }, 100);
                }}
              >
                <ListItemIcon>
                  <SvgIcon fontSize="small">
                    <CurrencyDollarIcon />
                  </SvgIcon>
                </ListItemIcon>
                <ListItemText
                  primary="Upgrade"
                  primaryTypographyProps={{ fontWeight: "bold" }}
                />
              </ListItemButton>
            )}
          {!pomeriumUser?.needsOnboarding && (
            <ListItemButton
              onClick={() => {
                popover.handleClose();
                setTimeout(() => {
                  navigate("/app/management/api-tokens", { replace: true });
                }, 100);
              }}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <FingerPrintIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText primary="API Tokens" />
            </ListItemButton>
          )}
          <ListItemButton
            onClick={() => {
              navigate("/logout", { replace: true });
            }}
          >
            <ListItemIcon>
              <SvgIcon fontSize="small">
                <ArrowRightOnRectangleIcon />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText primary="Log out" />
          </ListItemButton>
          <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
            <Stack alignContent="center">
              <ListItemText
                sx={{ pt: 1 }}
                secondary={
                  "api: " +
                  (apiVersion?.version || "").slice(0, 7) +
                  "  ui: " +
                  import.meta.env.VITE_UI_GIT_SHA?.slice(0, 7)
                }
              />
            </Stack>
          </Stack>
          <Footer />
        </List>
      </Popover>
    </>
  );
};
